import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Column, Filter, LocalView, Taxonomy } from "../types";
import { applyInMemoryFilters, clearFilter, isColumnFiltered } from "../utils";
import { useLocalViews, getViewsKey, getSelectedViewKey } from "./useLocalViews";
import { getCurrentUserId } from "../../../utils/currentUser";

const getActiveColumnsKey = (tableId: string): string =>
  `ram_column_options_active_${getCurrentUserId()}_${tableId}`;

/**
 * Reconstitute Date objects in date filters from ISO strings.
 */
const hydrateColumns = (columns: Column[]): Column[] => {
  return columns.map((col) => {
    if (!col.filter || col.filter.type !== "date") return col;
    return {
      ...col,
      filter: {
        ...col.filter,
        date1: col.filter.date1 ? new Date(col.filter.date1 as any) : null,
        date2: col.filter.date2 ? new Date(col.filter.date2 as any) : null,
      },
    };
  });
};

/**
 * Merge stored columns with defaults to handle columns added/removed since last save.
 * - Columns in defaults but not in stored are appended with default config.
 * - Columns in stored but not in defaults are removed.
 * - Stored column state (visibility, order, filters) is preserved.
 * - Non-serializable properties (onRender, etc.) are always taken from defaults.
 */
const mergeWithDefaults = (
  stored: Column[],
  defaults: Column[]
): Column[] => {
  const defaultMap = new Map(defaults.map((c) => [c.key, c]));
  const storedKeys = new Set(stored.map((c) => c.key));

  // Start with stored columns (preserving order), merged with default non-serializable props
  const merged: Column[] = stored
    .filter((c) => defaultMap.has(c.key))
    .map((c) => {
      const def = defaultMap.get(c.key)!;
      return {
        ...def,
        // Fixed/alwaysVisible columns are always visible regardless of stored state
        isVisible: (def.isFixed || def.isAlwaysVisible) ? true : c.isVisible,
        filter: c.filter ?? def.filter,
      };
    });

  // Append new default columns that weren't in stored
  for (const def of defaults) {
    if (!storedKeys.has(def.key)) {
      merged.push(def);
    }
  }

  return merged;
};

const loadActiveColumns = (
  tableId: string,
  defaults: Column[]
): Column[] => {
  try {
    const raw = localStorage.getItem(getActiveColumnsKey(tableId));
    if (!raw) return defaults;
    const stored: Column[] = JSON.parse(raw);
    return mergeWithDefaults(hydrateColumns(stored), defaults);
  } catch {
    return defaults;
  }
};

const saveActiveColumns = (tableId: string, columns: Column[]) => {
  // Only save serializable column state (key, isVisible, filter)
  const serializable = columns.map((c) => ({
    key: c.key,
    label: c.label,
    isVisible: c.isVisible,
    isFixed: c.isFixed,
    isPresentInColumnOptions: c.isPresentInColumnOptions,
    filter: c.filter,
  }));
  localStorage.setItem(
    getActiveColumnsKey(tableId),
    JSON.stringify(serializable)
  );
};

type UseColumnOptionsStateProps = {
  tableId: string;
  defaultColumns: Column[];
  taxonomy: { [key: string]: Taxonomy[] };
};

export const useColumnOptionsState = ({
  tableId,
  defaultColumns,
  taxonomy,
}: UseColumnOptionsStateProps) => {
  // Active columns = what the table actually uses
  const [activeColumns, setActiveColumns] = useState<Column[]>(() =>
    loadActiveColumns(tableId, defaultColumns)
  );

  // Draft columns = what the user is editing in the panel (initialized on panel open)
  const [draftColumns, setDraftColumns] = useState<Column[]>(activeColumns);

  // Panel open/close
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Views
  const {
    views,
    selectedView,
    selectView,
    createView: createViewInStorage,
    updateView: updateViewInStorage,
    saveViewColumns,
    deleteView: deleteViewFromStorage,
  } = useLocalViews({ tableId });

  // Track the default columns ref for merging
  const defaultColumnsRef = useRef(defaultColumns);
  defaultColumnsRef.current = defaultColumns;

  // Persist active columns
  useEffect(() => {
    saveActiveColumns(tableId, activeColumns);
  }, [tableId, activeColumns]);

  // --- Panel open/close ---
  const openPanel = useCallback(() => {
    setDraftColumns(activeColumns);
    setIsPanelOpen(true);
  }, [activeColumns]);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // --- Draft operations ---
  const onClickApply = useCallback(() => {
    setActiveColumns(draftColumns);
    setIsPanelOpen(false);
  }, [draftColumns]);

  const onReorderColumn = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      setDraftColumns((prev) => {
        const cols = [...prev];
        const [removed] = cols.splice(sourceIndex, 1);
        cols.splice(destinationIndex, 0, removed);
        return cols;
      });
    },
    []
  );

  const onToggleColumnVisibility = useCallback((columnKey: string) => {
    setDraftColumns((prev) =>
      prev.map((c) =>
        c.key === columnKey ? { ...c, isVisible: !c.isVisible } : c
      )
    );
  }, []);

  const onSelectAll = useCallback(() => {
    setDraftColumns((prev) =>
      prev.map((c) => (c.isFixed ? c : { ...c, isVisible: true }))
    );
  }, []);

  const onDeselectAll = useCallback(() => {
    setDraftColumns((prev) =>
      prev.map((c) => (c.isFixed || c.isAlwaysVisible ? c : { ...c, isVisible: false }))
    );
  }, []);

  const setFilter = useCallback(
    (columnKey: string, filter: Filter) => {
      setDraftColumns((prev) =>
        prev.map((c) => (c.key === columnKey ? { ...c, filter } : c))
      );
    },
    []
  );

  const onClickClearAllFilters = useCallback(() => {
    setDraftColumns((prev) =>
      prev.map((c) => {
        if (!c.filter) return c;
        return { ...c, filter: clearFilter(c.filter) ?? c.filter };
      })
    );
  }, []);

  const onClickReset = useCallback(() => {
    if (selectedView) {
      // Reset to the view's saved columns
      setDraftColumns(
        mergeWithDefaults(selectedView.columns, defaultColumnsRef.current)
      );
    } else {
      // Reset to defaults
      setDraftColumns(defaultColumnsRef.current);
    }
  }, [selectedView]);

  // --- View operations ---
  const onChangeView = useCallback(
    (viewId: string | null) => {
      selectView(viewId);
      if (viewId) {
        const view = views.find((v) => v.id === viewId);
        if (view) {
          const merged = mergeWithDefaults(
            view.columns,
            defaultColumnsRef.current
          );
          setDraftColumns(merged);
        }
      } else {
        // Cleared view selection - reset to defaults
        setDraftColumns(defaultColumnsRef.current);
      }
    },
    [views, selectView]
  );

  const onClickSaveView = useCallback(() => {
    saveViewColumns(draftColumns);
  }, [draftColumns, saveViewColumns]);

  const onCreateView = useCallback(
    (data: {
      name: string;
      description: string;
      isDefault: boolean;
    }) => {
      createViewInStorage({
        name: data.name,
        description: data.description,
        isDefault: data.isDefault,
        columns: draftColumns,
      });
    },
    [draftColumns, createViewInStorage]
  );

  const onUpdateView = useCallback(
    (data: {
      name: string;
      description: string;
      isDefault: boolean;
    }) => {
      updateViewInStorage(data);
    },
    [updateViewInStorage]
  );

  const onClickDeleteView = useCallback(() => {
    deleteViewFromStorage();
    setDraftColumns(defaultColumnsRef.current);
  }, [deleteViewFromStorage]);

  // --- Reset all localStorage for this table ---
  const onResetAllStorage = useCallback(() => {
    localStorage.removeItem(getActiveColumnsKey(tableId));
    localStorage.removeItem(getViewsKey(tableId));
    localStorage.removeItem(getSelectedViewKey(tableId));
    const defaults = defaultColumnsRef.current;
    setActiveColumns(defaults);
    setDraftColumns(defaults);
    selectView(null);
    setIsPanelOpen(false);
    // Force page reload to clear all stale state
    window.location.reload();
  }, [tableId, selectView]);

  // --- Data filtering (uses active columns) ---
  const applyFiltersToData = useCallback(
    <T extends Record<string, any>>(
      data: T[],
      getFieldValue?: (row: T, columnKey: string) => any
    ): T[] => {
      return applyInMemoryFilters(data, activeColumns, getFieldValue);
    },
    [activeColumns]
  );

  // --- Column queries ---
  const visibleColumns = useMemo(
    () => activeColumns.filter((c) => c.isVisible || c.isFixed),
    [activeColumns]
  );

  const activeFilterCount = useMemo(
    () =>
      activeColumns.filter((c) => c.filter && isColumnFiltered(c.filter))
        .length,
    [activeColumns]
  );

  return {
    // Panel state
    isPanelOpen,
    openPanel,
    closePanel,

    // For ColumnOptions component props
    columns: draftColumns,
    views,
    selectedView,
    taxonomy,
    isReady: true,
    isViewLoading: false,

    // Callbacks
    onClickApply,
    onChangeView,
    setFilter,
    onSelectAll,
    onDeselectAll,
    onReorderColumn,
    onToggleColumnVisibility,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    onCreateView,
    onUpdateView,
    onResetAllStorage,

    // Active (applied) state for the parent page
    activeColumns,
    visibleColumns,
    applyFiltersToData,
    activeFilterCount,
  };
};
